import { Waves, UtensilsCrossed, BedDouble, CookingPot, Car, Clock } from "lucide-react";
import { Reveal } from "./Reveal";
import { IconBadge } from "./IconBadge";

const SPECS = [
  { value: "02", label: "Private villas", testId: "villas-spec-villas" },
  { value: "06", label: "Bedrooms in each villa", testId: "villas-spec-bedrooms" },
  { value: "12 + 2", label: "Guests per villa", testId: "villas-spec-guests" },
  { value: "24 + 4", label: "Guests across both villas", testId: "villas-spec-total" },
];

const INCLUDED = [
  { icon: Waves, label: "Private swimming pool" },
  { icon: UtensilsCrossed, label: "Breakfast and dinner included" },
  { icon: BedDouble, label: "Six bedrooms, six bathrooms" },
  { icon: CookingPot, label: "Kitchen and air conditioning" },
  { icon: Car, label: "Free on-site parking" },
  { icon: Clock, label: "Check-in 3 PM · Check-out 11 AM" },
];

export const Villas = () => (
  <section id="villas" data-testid="villas-section" className="bg-white py-20 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 sm:px-8">
      <Reveal className="max-w-3xl">
        <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-moss">The villas</p>
        <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-brand-licorice sm:text-5xl lg:text-6xl">
          Two villas. <span className="italic text-brand-brunswick">One private escape.</span>
        </h2>
        <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-brand-licorice/70 sm:text-lg">
          Two identical private villas, each with six bedrooms, generous living spaces and a private pool. Made for
          family getaways, friend groups and celebrations that deserve room to unfold.
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-16 lg:mt-24">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-4">
          {SPECS.map((s) => (
            <div key={s.testId} data-testid={s.testId} className="border-t border-brand-licorice/15 pt-6">
              <p className="font-serif text-5xl font-light tracking-tight text-brand-licorice sm:text-6xl">{s.value}</p>
              <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-licorice/60">{s.label}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15} className="mt-16 lg:mt-24">
        <p className="mb-8 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-moss">What&rsquo;s included</p>
        <ul data-testid="villas-included" className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
          {INCLUDED.map((item, i) => (
            <li key={item.label} data-testid={`villas-included-${i}`} className="flex flex-col gap-4">
              <IconBadge icon={item.icon} delay={i * 0.06} />
              <span className="text-sm leading-snug text-brand-licorice/80">{item.label}</span>
            </li>
          ))}
        </ul>
        <p className="mt-10 font-serif text-lg italic text-brand-licorice/60">
          Lunch and safari experiences can be added to your stay.
        </p>
      </Reveal>

      <Reveal delay={0.2} className="mt-12 flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-10">
        <a
          href="#enquire"
          data-testid="villas-enquire-cta"
          className="inline-block bg-brand-rust px-10 py-4 text-center text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-ivory transition-colors hover:bg-brand-sepia"
        >
          Enquire about your stay
        </a>
        <p data-testid="villas-price" className="font-serif text-xl text-brand-licorice">
          From ₹60,000 <span className="text-base italic text-brand-licorice/60">per villa, per night</span>
        </p>
      </Reveal>
    </div>
  </section>
);
